import _styled from "styled-components";
const _excluded = ["style", "className", "variant", "container", "renderTrigger", "overlayProps", "initialValueSelected"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import { FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import PropTypes from 'prop-types';
import { VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { DATE_PICKER_VARIANTS } from './consts';
import useDatePicker from './hooks/useDatePicker';
import Footer from './layout/Footer';
import DatePickerOverlay from './layout/Overlay';
import Trigger from './layout/Trigger';
import TriggerWrapper from './layout/TriggerWrapper';
/**
 * The Date Picker is a basic component that allows the user to pick a single date.
 * Date pickers allow the selection of specific dates and years. It displays a default
 * text field and a dropdown calendar appears when a user taps on the text field.
 */
const DatePicker = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      variant = DATE_PICKER_VARIANTS.FIELD,
      container = document.body,
      renderTrigger = p => /*#__PURE__*/React.createElement(Trigger, p),
      overlayProps = {},
      initialValueSelected = true
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const target = useForwardedRef(ref);
  const [shouldShowSelection, setShouldShowSelection] = React.useState(initialValueSelected);
  const datePickerData = useDatePicker(_objectSpread({
    variant,
    target,
    setShouldShowSelection
  }, props));
  const {
    context,
    open,
    targetProps,
    placement,
    getTriggerProps,
    arrowProps,
    overlayContainerProps
  } = datePickerData;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_StyledTriggerWrapper, _extends({
    className: className
  }, targetProps, {
    $_css: style !== null ? style : undefined
  }), renderTrigger(_objectSpread({}, getTriggerProps()))), /*#__PURE__*/React.createElement(FloatingPortal, {
    root: container
  }, open && /*#__PURE__*/React.createElement(FloatingFocusManager, {
    context: context,
    modal: false
  }, /*#__PURE__*/React.createElement("div", overlayContainerProps, variant === DATE_PICKER_VARIANTS.ICON && /*#__PURE__*/React.createElement(Overlay.Arrow, _extends({
    placement: placement,
    color: theme.tokens.colors.background.primary.value
  }, arrowProps)), /*#__PURE__*/React.createElement(DatePickerOverlay, _extends({
    double: props.double,
    timeZone: props.timeZone,
    calendarProps: props.calendarProps,
    onMonthChange: props.onMonthChange,
    showOutsideDays: props.showOutsideDays,
    isDateSelectable: props.isDateSelectable,
    yearsToDisplay: props.yearsToDisplay,
    firstDayOfWeek: props.firstDayOfWeek,
    tooltipContentForDate: props.tooltipContentForDate,
    hasEventForDate: props.hasEventForDate,
    renderFooter: props.renderFooter,
    renderFooterActions: props.renderFooterActions,
    minDate: datePickerData.minDate,
    shouldShowSelection: shouldShowSelection,
    maxDate: datePickerData.maxDate,
    value: datePickerData.date,
    startDate: null,
    endDate: null,
    toggleOverlay: datePickerData.toggleOverlay,
    onDateSelected: datePickerData.onDateSelected,
    resetSelection: datePickerData.resetSelection
  }, overlayProps))))));
});
DatePicker.displayName = 'DatePicker';
DatePicker.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Variant of the field could be icon or field */
  variant: PropTypes.oneOf(Object.values(DATE_PICKER_VARIANTS)),
  /** A selected date for a calendar */
  value: PropTypes.instanceOf(Date),
  /** The initial date for calendar */
  initialValue: PropTypes.instanceOf(Date),
  /** When true, initialValue will be selected, when false calendar will be opened to initialValue */
  initialValueSelected: PropTypes.bool,
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Whether we should disable dates before today */
  disablePastDates: PropTypes.bool,
  /** Whether we should disable dates after today */
  disableFutureDates: PropTypes.bool,
  /** A callback function called when the selected date(s) has been changed */
  onChange: PropTypes.func,
  /** A callback function called when the active months have changed (arrow click, month/year selection) */
  onMonthChange: PropTypes.func,
  /** Handler to manually check if date is selectable */
  isDateSelectable: PropTypes.func,
  /** The format to display the date with */
  format: PropTypes.oneOf(Object.values(VALID_DATE_FORMATS)),
  /** Whether picker should be open initially */
  initialOpen: PropTypes.bool,
  /** Controlled picker open state */
  open: PropTypes.bool,
  /** Whether we should display double date picker */
  double: PropTypes.bool,
  /** Whether the input should display in error state */
  error: PropTypes.bool,
  /** Disable all functionality */
  disabled: PropTypes.bool,
  /** First day of week to display */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /** Fill the blanks with days from prev/next month */
  showOutsideDays: PropTypes.bool,
  /** Passed to the field, placeholder to display when there is no value */
  placeholder: PropTypes.string,
  /** Props passed to DatePickerField or Button (when variant is icon) */
  triggerProps: PropTypes.object,
  /** Props passed to DatePickerOverlay */
  overlayProps: PropTypes.object,
  /** Props passed to each calendar */
  calendarProps: PropTypes.object,
  /** Render custom footer, executed in DatePickerOverlay */
  renderFooter: PropTypes.func,
  /** Render custom footer actions, executed in DatePickerOverlay */
  renderFooterActions: PropTypes.func,
  /** Render custom footer, executed in DatePicker */
  renderTrigger: PropTypes.func,
  /** Determines if Day Cell should render an event dot */
  hasEventForDate: PropTypes.func,
  /** Tooltip content for Day Cell */
  tooltipContentForDate: PropTypes.func,
  /** How many years to display under and above current year, defaults to +-20 */
  yearsToDisplay: PropTypes.number,
  /** Specific time zone for this DatePicker, overriding the user's time zone */
  timeZone: PropTypes.string,
  /** Get the container element for the overlay element */
  container: PropTypes.any,
  /** A callback function called when DatePicker overlay is being hidden (closed) */
  onHide: PropTypes.func,
  /** Options that modify how the calendar overlay can shift (see floating-ui documentation https://floating-ui.com/docs/shift) */
  shiftArgs: PropTypes.any,
  /** Options that modify how the calendar overlay can flip (see floating-ui documentation https://floating-ui.com/docs/flip) */
  flipArgs: PropTypes.any
};
export default Object.assign(DatePicker, {
  VARIANTS: DATE_PICKER_VARIANTS,
  VALID_FORMATS: VALID_DATE_FORMATS,
  Footer,
  Trigger
});
var _StyledTriggerWrapper = _styled(TriggerWrapper).withConfig({
  displayName: "DatePicker___StyledTriggerWrapper",
  componentId: "sc-fbhvcg-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=DatePicker.js.map